// Servicio para la gestión de solicitudes de asuntos propios de los empleados, asociados con un usuario. Comunicación con la API.
// Se importa en services.module.ts

import { Injectable } from '@angular/core';
import { BaseService } from '../base';
import { HttpClient } from '@angular/common/http';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ApiResponse } from '../../models/base';
import { AsuntosPropios } from '../../models/personal/asuntos-propios.models';

const ASUNTOS_PROPIOS_ENDPOINT = '/asuntos-propios/';

@Injectable({
  providedIn: 'root',
})
export class AsuntosPropiosService extends BaseService {
  // #region Propiedades del servicio
  loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private asuntosPropios: AsuntosPropios[] = [];

  asuntosPropiosSolicitados: AsuntosPropios[] = null;
  vs$: BehaviorSubject<AsuntosPropios[]> = new BehaviorSubject<AsuntosPropios[]>([]);
  asuntosPropiosSolicitados$: Observable<AsuntosPropios[]> = this.vs$.asObservable();

  asuntosPropiosAprobados: AsuntosPropios[] = null;
  ap$: BehaviorSubject<AsuntosPropios[]> = new BehaviorSubject<AsuntosPropios[]>([]);
  asuntosPropiosAprobados$: Observable<AsuntosPropios[]> = this.ap$.asObservable();
  // #endregion

  constructor(private http: HttpClient, protected snackBar: MatSnackBar) {
    super(snackBar);
  }

  // #region Métodos de consulta a la API

  /**
   * Método para eliminar una solicitud de asuntos propios.
   * @param id Identificador de la solicitud
   */
  deleteAsuntosPropios(id: number): Observable<boolean> {
    this.loading$.next(true);

    return this.http
      .delete<ApiResponse<boolean>>(environment.apiUrl + ASUNTOS_PROPIOS_ENDPOINT + id)
      .pipe(
        map((resultado) => {
          if (resultado && resultado.data) {
            this.asuntosPropios = this.asuntosPropios.filter((v) => v.id != id);
            this.procesarAsuntosPropios();
            return true;
          }

          this.procesarError(resultado);
          throw resultado;
        }),
        tap(() => {
          this.snackBar.open('Asuntos propios correctamente eliminados', undefined, {
            duration: 3000,
          });
        }),
        catchError((error) => {
          this.procesarError(error && error.error ? error.error : error);
          return throwError(error);
        }),
        finalize(() => {
          this.loading$.next(false);
        })
      );
  }

  /**
   * Método para obtener los asuntos propios de los empleados asociados con un usuario.
   */
  getAsuntosPropios(): void {
    this.loading$.next(true);

    this.http
      .get<ApiResponse<AsuntosPropios[]>>(environment.apiUrl + ASUNTOS_PROPIOS_ENDPOINT)
      .pipe(
        finalize(() => {
          this.loading$.next(false);
        })
      )
      .subscribe({
        next: (resultado) => {
          if (resultado && resultado.data) {
            this.asuntosPropios = resultado.data;
            this.procesarAsuntosPropios();
          } else if (resultado && resultado.error) {
            this.procesarError(resultado);
          }
        },
        error: (error) => {
          this.procesarError(error);
        },
      });
  }

  /**
   * Método para obtener los asuntos propios de un empleado.
   * @param id Identificador del empleado
   */
  getAsuntosPropiosPorEmpleado(id: number): Observable<AsuntosPropios[]> {
    this.loading$.next(true);

    return this.http
      .get<ApiResponse<AsuntosPropios[]>>(
        environment.apiUrl + ASUNTOS_PROPIOS_ENDPOINT + 'empleado/' + id
      )
      .pipe(
        map((resultado) => {
          if (resultado && resultado.data) {
            return resultado.data;
          }

          this.procesarError(resultado);
          throw resultado;
        }),
        tap((data) => {
          if (data.length === 0) {
            this.snackBar.open(
              'No hay asuntos propios para el empleado seleccionado',
              undefined,
              { duration: 3000 }
            );
          }
        }),
        catchError((error) => {
          this.procesarError(error && error.error ? error.error : error);
          return throwError(error);
        }),
        finalize(() => {
          this.loading$.next(false);
        })
      );
  }

  /**
   * Método para obtener los asuntos propios en un rango de fechas.
   * @param inicio Fecha de inicio del rango
   * @param fin Fecha de fin del rango
   */
  getAsuntosPropiosPorFechas(
    inicio: string,
    fin: string
  ): Observable<AsuntosPropios[]> {
    return this.http
      .get<ApiResponse<AsuntosPropios[]>>(
        environment.apiUrl +
          ASUNTOS_PROPIOS_ENDPOINT +
          'rango/' +
          inicio +
          '/' +
          fin
      )
      .pipe(
        map((resultado) => {
          if (resultado && resultado.data) {
            return resultado.data;
          }

          throw resultado;
        }),
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  newAsuntosPropios(): AsuntosPropios {
    return {
      id: 0,
      cadenaFechaAprobacion: '',
      cadenaFechaRechazo: '',
      cadenaFechaSolicitud: '',
      cadenaFin: '',
      cadenaInicio: '',
      employees_Id: undefined,
      estado: 2,
      fechaAprobacion: null,
      fechaRechazo: null,
      fechaSolicitud: null,
      fin: null,
      inicio: null,
      motivo: '',
      motivoRechazo: '',
      users_id: 0,
      nombreEstado: '',
    };
  }

  /**
   * Método para guardar una solicitud de asuntos propios.
   * @param asuntosPropios Solicitud a guardar
   */
  saveAsuntosPropios(asuntosPropios: AsuntosPropios): Observable<AsuntosPropios> {
    this.loading$.next(true);

    const request$ =
      asuntosPropios.id > 0
        ? this.http.put<ApiResponse<AsuntosPropios>>(
            environment.apiUrl + ASUNTOS_PROPIOS_ENDPOINT,
            asuntosPropios
          )
        : this.http.post<ApiResponse<AsuntosPropios>>(
            environment.apiUrl + ASUNTOS_PROPIOS_ENDPOINT,
            asuntosPropios
          );

    return request$.pipe(
      map((resultado) => {
        if (resultado && resultado.data) {
          const existente = this.asuntosPropios.find((v) => v.id == resultado.data.id);

          if (existente == null) {
            this.asuntosPropios.push(resultado.data);
          } else {
            this.asuntosPropios = this.asuntosPropios.map((v) =>
              v.id == resultado.data.id ? resultado.data : v
            );
          }

          this.procesarAsuntosPropios();
          return resultado.data;
        }

        this.procesarError(resultado);
        throw resultado;
      }),
      tap(() => {
        this.snackBar.open('Asuntos propios correctamente guardados', undefined, {
          duration: 3000,
        });
      }),
      catchError((error) => {
        this.procesarError(error && error.error ? error.error : error);
        return throwError(error);
      }),
      finalize(() => {
        this.loading$.next(false);
      })
    );
  }

  // #endregion

  // #region Métodos privados

  /**
   * Método para procesar los asuntos propios obtenidos de la API.
   */
  private procesarAsuntosPropios(): void {
    this.asuntosPropiosSolicitados = this.asuntosPropios.filter((v) => v.estado == 1);
    this.vs$.next(this.asuntosPropiosSolicitados);

    this.asuntosPropiosAprobados = this.asuntosPropios.filter((v) => v.estado == 2);
    this.ap$.next(this.asuntosPropiosAprobados);
  }

  // #endregion
}